export type VttItem = { lang_code: string; lang_file: string };

// function parseWebVTT(raw: string): { start: number; end: number; text: string }[] {
//   const s = raw.replace(/\r/g, '');
//   const lines = s.split('\n');
//   const cues: { start: number; end: number; text: string }[] = [];
//   let i = 0;
//   if (lines[i]?.startsWith('\uFEFF')) lines[i] = lines[i].slice(1);
//   if (lines[i]?.toUpperCase().startsWith('WEBVTT')) i++;
//   while (i < lines.length && lines[i].trim() !== '') i++;
//   const timeReHM = /(\d{2}):(\d{2})(?:\.(\d{1,3}))?\s*-->\s*(\d{2}):(\d{2})(?:\.(\d{1,3}))?/;
//   const timeReHMS = /(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?\s*-->\s*(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?/;
//   const toSec = (h: string, m: string, sec: string, ms?: string) =>
//     (h ? parseInt(h) * 3600 : 0) + parseInt(m) * 60 + parseFloat(sec) + (ms ? parseInt(ms.padEnd(3, '0')) / 1000 : 0);

//   while (i < lines.length) {
//     while (i < lines.length && lines[i].trim() === '') i++;
//     if (i >= lines.length) break;
//     if (!lines[i].includes('-->')) i++;
//     if (i >= lines.length) break;

//     const tl = lines[i++];
//     const m = tl.match(timeReHMS);
//     let start = 0, end = 0;
//     if (m) {
//       start = toSec(m[1], m[2], m[3], m[4]);
//       end = toSec(m[5], m[6], m[7], m[8]);
//     } else {
//       const m2 = tl.match(timeReHM);
//       if (!m2) { while (i < lines.length && lines[i].trim() !== '') i++; continue; }
//       start = toSec('00', m2[1], m2[2], m2[3]);
//       end = toSec('00', m2[4], m2[5], m2[6]);
//     }
//     const textLines: string[] = [];
//     while (i < lines.length && lines[i].trim() !== '') textLines.push(lines[i++]);
//     cues.push({ start, end, text: textLines.join('\n') });
//   }
//   return cues;
// }

// const buildVttSrc = (baseUrl: string, epiId?: string) =>
//   epiId ? `${baseUrl}?v=${epiId}` : baseUrl;

type CueConstructor = new (startTime: number, endTime: number, text: string) => VTTCue;

function getCueConstructor(): CueConstructor {
  const w = window as unknown as { VTTCue?: CueConstructor; TextTrackCue?: CueConstructor };
  return (typeof w.VTTCue === 'function' ? w.VTTCue : w.TextTrackCue)!;
}

// Reuse an existing programmatic subtitles track for the same language if present.
// This avoids piling up multiple addTextTrack tracks across episodes.
function getOrCreateLangTrack(videoEl: HTMLVideoElement, lang: string, label: string): TextTrack {
  // Try to find an existing programmatically created track with same language
  for (let i = 0; i < videoEl.textTracks.length; i++) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tt = videoEl.textTracks[i] as any;
    if (tt.kind === 'subtitles' && (tt.language === lang || tt.label?.toLowerCase() === label.toLowerCase())) {
      // Clear previous cues
      const cues = (tt as TextTrack).cues;
      if (cues) {
        while (cues.length > 0) {
          try { (tt as TextTrack).removeCue(cues[0] as VTTCue); } catch {}
        }
      }
      return tt as TextTrack;
    }
  }
  // Create a fresh programmatic track
  return videoEl.addTextTrack('subtitles', label, lang);
}

export async function ensureSubtitlesVisible(opts: {
  videoEl: HTMLVideoElement | null | undefined;
  vttSubtitles: VttItem[];
  selectedLang?: string;
  episodeId?: string;
  useProxy?: boolean;
}): Promise<boolean> {
  const { videoEl, vttSubtitles, selectedLang, episodeId, useProxy } = opts;
  if (!videoEl) return false;

  // If any native cues exist, bypass
  let totalCues = 0;
  for (let i = 0; i < videoEl.textTracks.length; i++) {
    totalCues += videoEl.textTracks[i].cues ? videoEl.textTracks[i].cues!.length : 0;
  }
  if (totalCues > 0) return true;

  const lang = (selectedLang || vttSubtitles[0]?.lang_code || '').toLowerCase();
  const chosen = vttSubtitles.find(s => s.lang_code.toLowerCase() === lang) || vttSubtitles[0];
  if (!chosen) return false;

  const upstream = episodeId ? `${chosen.lang_file}?v=${episodeId}` : chosen.lang_file;
  const url = useProxy ? `/api/proxy-vtt?url=${encodeURIComponent(upstream)}` : upstream;

  try {
    const resp = await fetch(url, { mode: 'cors', cache: 'no-store' });
    const text = await resp.text();

    // Parse + dedupe
    const raw = text.replace(/\r/g, '');
    const cues = raw
      .split(/\n\n+/)
      .map(block => {
        const lines = block.split('\n').filter(Boolean);
        if (!lines.some(l => l.includes('-->'))) return null;
        const time = lines.find(l => l.includes('-->'))!;
        const m = time.match(/(?:(\d{2}):)?(\d{2}):(\d{2})(?:\.(\d{1,3}))?\s*-->\s*(?:(\d{2}):)?(\d{2}):(\d{2})(?:\.(\d{1,3}))?/);
        if (!m) return null;
        const toSec = (h?: string, m?: string, s?: string, ms?: string) =>
          (h ? parseInt(h, 10) * 3600 : 0) +
          parseInt(m || '0', 10) * 60 +
          parseFloat(s || '0') +
          (ms ? parseInt(ms.padEnd(3, '0'), 10) / 1000 : 0);
        const start = toSec(m[1], m[2], m[3], m[4]);
        const end = toSec(m[5], m[6], m[7], m[8]);
        const txt = lines.slice(lines.indexOf(time) + 1).join('\n');
        return { start, end, text: txt };
      })
      .filter(Boolean) as { start: number; end: number; text: string }[];

    const norm = (t: string) => t.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
    const seen = new Set<string>();
    const unique = cues.filter(c => {
      const key = `${c.start.toFixed(3)}|${c.end.toFixed(3)}|${norm(c.text)}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    // Reuse or create a subtitles track for this language, then inject cues
    const label = (chosen.lang_code || 'sub').toUpperCase();
    const tt = getOrCreateLangTrack(videoEl, chosen.lang_code, label);
    tt.mode = 'showing';

    const CueCtor = getCueConstructor();
    unique.forEach(c => {
      try { tt.addCue(new CueCtor(c.start, c.end, c.text)); } catch {}
    });

    return unique.length > 0;
  } catch {
    return false;
  }
}